/**
 * Bouton de suppression d'un widget avec mini-confirmation en ligne.
 * - 1er clic : le bouton se transforme en pastille rouge « Supprimer ? ».
 * - 2e clic (sur la pastille) : confirme → onDelete().
 * - clic en dehors, touche Échap, ou inaction (quelques secondes) : annule.
 *
 * Pas d'appui long, pas de fenêtre modale : confirmation légère, directement
 * sur le widget.
 */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";

/** Délai après lequel une confirmation non suivie d'effet est annulée. */
const CONFIRM_TIMEOUT_MS = 3000;

export function DeleteWidgetButton({ onDelete }: { onDelete: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-annulation après quelques secondes d'inaction + nettoyage du clic
  // extérieur, regroupés tant que la confirmation est active.
  useEffect(() => {
    if (!confirming) return;

    const timer = setTimeout(() => setConfirming(false), CONFIRM_TIMEOUT_MS);
    const onPointerDownOutside = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setConfirming(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDownOutside, true);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("pointerdown", onPointerDownOutside, true);
    };
  }, [confirming]);

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (confirming) {
      onDelete();
    } else {
      setConfirming(true);
    }
  };

  return (
    <div ref={containerRef} className="flex items-center">
      <button
        type="button"
        onClick={handleClick}
        onKeyDown={(event) => {
          if (event.key === "Escape") setConfirming(false);
        }}
        className={
          confirming
            ? "h-7 px-2.5 rounded-full flex items-center gap-1.5 bg-red-600 text-white transition-colors"
            : "w-7 h-7 rounded-full flex items-center justify-center text-grey-medium hover:text-red-500 hover:bg-grey-light/60 transition-colors"
        }
        aria-label={
          confirming
            ? "Confirmer la suppression du widget"
            : "Supprimer le widget"
        }
        title={confirming ? "Cliquer pour confirmer" : "Supprimer le widget"}
      >
        <FontAwesomeIcon icon="trash-can" className="h-4 w-4 shrink-0" />
        {confirming && (
          <span className="text-xs font-oxaniumMedium whitespace-nowrap">
            Supprimer ?
          </span>
        )}
      </button>
    </div>
  );
}
